import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useClipboard,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { FiCopy, FiMonitor, FiSlash } from 'react-icons/fi';

import {
  LossControlDisplayDevice,
  lossControlService,
} from '../../../services/lossControlService';
import {
  buildLossControlDisplayPath,
  LossControlDisplayMode,
} from './displayMode';

interface DisplayAccessManagerProps {
  branchId: string;
  branchName: string;
  displayMode: LossControlDisplayMode;
  isDisabled?: boolean;
}

function formatDate(value: string | null): string {
  if (!value) return 'Sem vencimento';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

const DisplayAccessManager: React.FC<DisplayAccessManagerProps> = ({
  branchId,
  branchName,
  displayMode,
  isDisabled = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [devices, setDevices] = useState<LossControlDisplayDevice[]>([]);
  const [deviceName, setDeviceName] = useState('');
  const [neverExpires, setNeverExpires] = useState(true);
  const [bootstrapUrl, setBootstrapUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState('');
  const { hasCopied, onCopy } = useClipboard(bootstrapUrl);

  const loadDevices = useCallback(async () => {
    if (!branchId) return;

    setLoading(true);
    try {
      setDevices(await lossControlService.listDisplayDevices(branchId));
    } catch (error) {
      setDevices([]);
      toast({
        title: 'Não foi possível consultar as TVs.',
        description: 'Verifique sua permissão e tente novamente.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [branchId, toast]);

  useEffect(() => {
    if (!isOpen) return;

    setDeviceName(`TV filial ${branchId}`);
    setBootstrapUrl('');
    loadDevices();
  }, [branchId, isOpen, loadDevices]);

  const createDevice = async () => {
    const normalizedName = deviceName.trim();
    if (!normalizedName) {
      toast({ title: 'Informe o nome da TV.', status: 'warning' });
      return;
    }

    setCreating(true);
    try {
      const access = await lossControlService.createDisplayAccess(branchId, {
        deviceName: normalizedName,
        neverExpires,
      });
      const displayPath = buildLossControlDisplayPath({
        branchId,
        mode: displayMode,
        demo: false,
        token: access.token,
      });

      setBootstrapUrl(`${window.location.origin}${displayPath}`);
      await loadDevices();
      toast({
        title: 'Acesso da TV criado.',
        description: 'Copie o link agora. O token não será exibido novamente.',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Não foi possível criar o acesso da TV.',
        description: 'Verifique sua permissão e tente novamente.',
        status: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const revokeDevice = async (device: LossControlDisplayDevice) => {
    // A confirmação nativa impede revogação acidental sem manter outro modal aberto.
    // eslint-disable-next-line no-alert
    const shouldRevoke = window.confirm(
      `Revogar o acesso de "${device.deviceName}"? A TV deixará de atualizar.`,
    );

    if (!shouldRevoke) {
      return;
    }

    setRevokingId(device.id);
    try {
      await lossControlService.revokeDisplayDevice(device.id);
      await loadDevices();
      toast({ title: 'Acesso da TV revogado.', status: 'success' });
    } catch (error) {
      toast({
        title: 'Não foi possível revogar o acesso.',
        status: 'error',
      });
    } finally {
      setRevokingId('');
    }
  };

  const closeManager = () => {
    setBootstrapUrl('');
    onClose();
  };

  return (
    <>
      <Button
        leftIcon={<FiMonitor />}
        variant="outline"
        onClick={onOpen}
        isDisabled={isDisabled || !branchId}
      >
        Configurar TVs
      </Button>

      <Modal isOpen={isOpen} onClose={closeManager} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Configurar TVs - {branchName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction={{ base: 'column', md: 'row' }} gridGap="4">
              <FormControl flex="1">
                <FormLabel>Nome do dispositivo</FormLabel>
                <Input
                  value={deviceName}
                  maxLength={100}
                  onChange={event => setDeviceName(event.target.value)}
                  placeholder="Ex.: tv-logistica-01"
                />
                <FormHelperText>
                  Use o nome configurado no Raspberry para facilitar a
                  identificação.
                </FormHelperText>
              </FormControl>

              <FormControl w={{ base: '100%', md: '220px' }}>
                <FormLabel>Validade</FormLabel>
                <Flex align="center" minH="40px" gridGap="2">
                  <Switch
                    isChecked={neverExpires}
                    onChange={event => setNeverExpires(event.target.checked)}
                  />
                  <Text>Sem expiração automática</Text>
                </Flex>
              </FormControl>

              <Button
                colorScheme="green"
                alignSelf={{ base: 'stretch', md: 'flex-end' }}
                onClick={createDevice}
                isLoading={creating}
              >
                Gerar acesso
              </Button>
            </Flex>

            {bootstrapUrl && (
              <Alert status="warning" mt="5" alignItems="flex-start">
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle>Link exibido uma única vez</AlertTitle>
                  <AlertDescription>
                    Copie este link para configurar o Raspberry. Não envie por
                    canais públicos.
                  </AlertDescription>
                  <Flex mt="3" gridGap="2">
                    <Input
                      value={bootstrapUrl}
                      readOnly
                      type="password"
                      bg="white"
                    />
                    <Button leftIcon={<FiCopy />} onClick={onCopy}>
                      {hasCopied ? 'Copiado' : 'Copiar link'}
                    </Button>
                  </Flex>
                </Box>
              </Alert>
            )}

            <Box mt="6" overflowX="auto">
              <Flex justify="space-between" align="center" mb="3">
                <Text fontWeight="bold">Acessos cadastrados</Text>
                <Button size="sm" variant="ghost" onClick={loadDevices}>
                  Atualizar lista
                </Button>
              </Flex>

              {loading ? (
                <Flex justify="center" py="8">
                  <Spinner />
                </Flex>
              ) : (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Dispositivo</Th>
                      <Th>Situação</Th>
                      <Th>Validade</Th>
                      <Th>Cadastro</Th>
                      <Th textAlign="right">Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {devices.map(device => (
                      <Tr key={device.id}>
                        <Td fontWeight="semibold">{device.deviceName}</Td>
                        <Td>
                          <Badge colorScheme={device.active ? 'green' : 'gray'}>
                            {device.active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </Td>
                        <Td>{formatDate(device.expiresAt)}</Td>
                        <Td>{formatDate(device.createdAt)}</Td>
                        <Td textAlign="right">
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            leftIcon={<FiSlash />}
                            isDisabled={!device.active}
                            isLoading={revokingId === device.id}
                            onClick={() => revokeDevice(device)}
                          >
                            Revogar
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                    {devices.length === 0 && (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py="8">
                          Nenhuma TV cadastrada para esta filial.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeManager}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DisplayAccessManager;
